import PageHeader from 'components/general/pageHeader';
import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { apiGet } from 'services';
import { Loading, SystemText } from '@salesperformanceint/e4shared';
import { setPrismMenu } from 'actions/ui';
import { EmptyState } from './components/emptyState';
import ExploreAll from './components/exploreAll';
import Completed from './components/completed';
import InProgress from './components/inProgress';
import ButtonGroup from './components/buttonGroup';
import { translateCompetency, translateCourse } from 'helpers/translations';
import './library.css';

const SearchBox = ({ search, setSearch, placeholder }) => {
    return (
        <div className="search-field">
            <i className="fa fa-search"></i>
            <input type="text" value={search} onChange={e => setSearch(e.target.value)} placeholder={placeholder} />
        </div>
    );
};

export const Library = () => {
    const dispatch = useDispatch();
    const [data, setData] = React.useState({});
    const [originalData, setOriginalData] = React.useState({});
    const [loading, setLoading] = React.useState(true);
    const [error, setError] = React.useState(false);
    const [search, setSearch] = React.useState('');
    const { user } = useSelector(state => state.user);
    const [section, setSection] = React.useState('AllBehaviours');

    React.useEffect(() => {
        dispatch(setPrismMenu('Library'));
    }, []);

    React.useEffect(() => {
        if (user?.guid) {
            loadLibraryData();
        }
    }, [user?.guid]);

    const loadLibraryData = () => {
        setLoading(true);
        setError(false);

        apiGet(
            `library/${user.id}`,
            user.guid,
            response => {
                const responseData = response || {};

                if (responseData.inProgressCourses) {
                    responseData.inProgressCourses = responseData.inProgressCourses.map(c =>
                        translateCourse(user.language_Code, c)
                    );
                }
                if (responseData.completedCourses) {
                    responseData.completedCourses = responseData.completedCourses.map(c =>
                        translateCourse(user.language_Code, c)
                    );
                }
                if (responseData.exploreAllBehaviours) {
                    responseData.exploreAllBehaviours = responseData.exploreAllBehaviours.map(b =>
                        translateCompetency(user.language_Code, b)
                    );
                }

                setData(responseData);
                setOriginalData(responseData);
                setLoading(false);
            },
            err => {
                console.error('Error loading library data:', err);
                setError(true);
                setLoading(false);
            }
        );
    };

    const text = React.useMemo(
        () => ({
            title: SystemText('Library/Heading/Title'),
            description: SystemText('Library/Description'),
            noResults: SystemText('Library/NoResults/Title'),
            noResultsSummary: SystemText('Library/NoResults/Summary'),
            noInProgress: SystemText('Library/NoInProgress/Title'),
            noInProgressSummary: SystemText('Library/NoInProgress/Summary'),
            noCompleted: SystemText('Library/NoCompleted/Title'),
            noCompletedSummary: SystemText('Library/NoCompleted/Summary'),
            noAllBehaviours: SystemText('Library/NoAllBehaviours/Title'),
            noAllBehavioursSummary: SystemText('Library/NoAllBehaviours/Summary'),
            error: SystemText('Library/Error/Title'),
            errorSummary: SystemText('Library/Error/Summary'),
            retry: SystemText('Library/Error/Retry'),
            search: SystemText('Library/Search/Label'),
            categoryFilter: SystemText('Library/Filter/Category'),
            sortByTitle: SystemText('Library/Sort/Title'),
            sortByCategory: SystemText('Library/Sort/Category'),
            sortByDate: SystemText('Library/Sort/Date'),
            groupByCategory: SystemText('Library/Toggle/GroupByCategory'),
            refresh: SystemText('Library/Buttons/Refresh'),
            inprogress: SystemText('Library/Heading/InProgress'),
            recommended: SystemText('Library/Heading/Recommended'),
            recommendedSummary: SystemText('Library/Summary/Recommended'),
            completed: SystemText('Library/Heading/Completed'),
            explore: SystemText('Library/Heading/ExploreAll'),
            behaviourSearchPlaceholder: SystemText('Library/Placeholder/SearchBehaviours'),
        }),
        []
    );

    React.useEffect(() => {
        if (!originalData.exploreAllBehaviours) return; // Don't filter if no original data yet

        if (search.trim() === '') {
            // Reset to original data when search is empty
            setData(originalData);
        } else {
            const filteredData =
                originalData.exploreAllBehaviours?.filter(b => {
                    return (
                        b.name.toLowerCase().indexOf(search.toLowerCase()) >= 0 ||
                        b.area.toLowerCase().indexOf(search.toLowerCase()) >= 0
                    );
                }) || [];

            setData({ ...originalData, exploreAllBehaviours: filteredData });
        }
    }, [search, originalData]);

    return (
        <div id="library-page" data-testid="library-page">
            <PageHeader title={text.title} />

            <ButtonGroup
                section={section}
                setSection={setSection}
                text={{
                    allBehaviours: SystemText('Library/Button/AllBehaviours'),
                    inProgress: SystemText('Library/Button/InProgress'),
                    completed: SystemText('Library/Button/Completed'),
                    noBehavioursMatchingSearch: SystemText('Library/Message/NoBehavioursMatchingSearch'),
                }}
                data={[]}
                loading={loading}
            />

            {section === 'InProgress' && data.inProgressCourses?.length > 0 && (
                <div className="mt30" id="in-progress-section">
                    <InProgress text={text} data={data.inProgressCourses} />
                </div>
            )}

            {section === 'Completed' && data.completedCourses?.length > 0 && (
                <div className="mt30" id="completed-section">
                    <Completed text={text} data={data.completedCourses} />
                </div>
            )}

            {section == 'AllBehaviours' && !loading && !error && originalData.exploreAllBehaviours?.length > 0 && (
                <div className="mt30" id="explore-all-section">
                    <SearchBox search={search} setSearch={setSearch} placeholder={text.behaviourSearchPlaceholder} />
                    {data.exploreAllBehaviours?.length > 0 && (
                        <ExploreAll text={text} data={data.exploreAllBehaviours} loading={loading} />
                    )}
                    {data.exploreAllBehaviours?.length === 0 && search.trim() !== '' && (
                        <EmptyState title={text.noResults} message={text.noResultsSummary} />
                    )}
                </div>
            )}

            <section className="content mtm10">
                {loading && <Loading />}
                {error && (
                    <EmptyState
                        icon="exclamation-triangle"
                        title={text.error}
                        message={text.errorSummary}
                        action={
                            <button className="btn btn-primary" onClick={loadLibraryData}>
                                {text.retry}
                            </button>
                        }
                    />
                )}

                {section === 'AllBehaviours' && !loading && !error && !originalData?.exploreAllBehaviours?.length && (
                    <EmptyState title={text.noAllBehaviours} message={text.noAllBehavioursSummary} />
                )}

                {section === 'InProgress' && !loading && !error && !data?.inProgressCourses?.length && (
                    <EmptyState title={text.noInProgress} message={text.noInProgressSummary} />
                )}

                {section === 'Completed' && !loading && !error && !data?.completedCourses?.length && (
                    <EmptyState title={text.noCompleted} message={text.noCompletedSummary} />
                )}
            </section>
        </div>
    );
};

export default Library;
